import { Controller } from "@hotwired/stimulus"
import { errorHandler } from "@/services/error_handler_service"

interface SvgTransform {
  x_offset: number
  y_offset: number
  scale: number
  min_lng: number
  max_lat: number
  lng_range: number
  lat_range: number
}

interface BoundaryPoint {
  x: number
  y: number
}

// Connects to data-controller="top-down-view"
export default class extends Controller {
  static override targets = ["svg"]
  static override values = {
    gardenId: Number,
    transform: Object,
    boundaryPoints: Array
  }

  declare readonly svgTarget: SVGElement
  declare readonly gardenIdValue: number
  declare readonly transformValue: SvgTransform | Record<string, never>
  declare readonly hasTransformValue: boolean
  declare readonly boundaryPointsValue: BoundaryPoint[]

  private zoomLevel: number = 1
  private selectedPlantId: number | null = null

  // SVG Zoom configuration (separate from MapLibre zoom used in map views)
  private readonly ZOOM_FACTOR = 1.2
  private readonly MAX_ZOOM = 3    // Maximum 3x zoom in
  private readonly MIN_ZOOM = 0.5  // Minimum 0.5x zoom out (50%)

  // Drag state
  private readonly DRAG_THRESHOLD = 4  // SVG units before a move becomes a drag
  private isDragging = false
  private dragGroup: SVGGElement | null = null
  private dragPointerId = 0
  private dragStartSvgX = 0
  private dragStartSvgY = 0
  private dragGroupOriginalCx = 0
  private dragGroupOriginalCy = 0
  private dragPlacementId = 0
  private hasDragged = false

  // Bound event handlers (stored so we can removeEventListener on disconnect)
  private readonly onPointerDown = (e: PointerEvent) => this.handlePointerDown(e)
  private readonly onPointerMove = (e: PointerEvent) => this.handlePointerMove(e)
  private readonly onPointerUp   = (e: PointerEvent) => this.handlePointerUp(e)
  private readonly onPointerCancel = (e: PointerEvent) => this.handlePointerCancel(e)
  private readonly onGardenPlantCardClose = () => {
    this.clearSelection({ notifyPanel: false })
  }

  private gardenBody: HTMLElement | null = null

  override connect() {
    this.setupSVGViewBox()
    this.svgTarget.addEventListener('pointerdown',   this.onPointerDown)
    this.svgTarget.addEventListener('pointermove',   this.onPointerMove)
    this.svgTarget.addEventListener('pointerup',     this.onPointerUp)
    this.svgTarget.addEventListener('pointercancel', this.onPointerCancel)
    this.gardenBody = this.element.closest('.garden__body')
    this.gardenBody?.addEventListener('gardenPlantCardClose', this.onGardenPlantCardClose)
  }

  override disconnect() {
    this.svgTarget.removeEventListener('pointerdown',   this.onPointerDown)
    this.svgTarget.removeEventListener('pointermove',   this.onPointerMove)
    this.svgTarget.removeEventListener('pointerup',     this.onPointerUp)
    this.svgTarget.removeEventListener('pointercancel', this.onPointerCancel)
    this.gardenBody?.removeEventListener('gardenPlantCardClose', this.onGardenPlantCardClose)
    this.gardenBody = null
  }

  // ---------------------------------------------------------------------------
  // Zoom controls
  // ---------------------------------------------------------------------------

  zoomIn() {
    this.zoomLevel = Math.min(this.zoomLevel * this.ZOOM_FACTOR, this.MAX_ZOOM)
    this.updateZoom()
  }

  zoomOut() {
    this.zoomLevel = Math.max(this.zoomLevel / this.ZOOM_FACTOR, this.MIN_ZOOM)
    this.updateZoom()
  }

  resetZoom() {
    this.zoomLevel = 1
    this.updateZoom()
  }

  private updateZoom() {
    const scale  = 1 / this.zoomLevel
    const offset = (400 * (1 - scale)) / 2
    this.svgTarget.setAttribute("viewBox", `${offset} ${offset} ${400 * scale} ${400 * scale}`)
  }

  // ---------------------------------------------------------------------------
  // Plant click selection (Stimulus data-action handlers on circles)
  // ---------------------------------------------------------------------------

  selectPlant(event: Event) {
    // Ignore if this pointer-up resolved a drag rather than a click
    if (this.hasDragged) return

    const target = event.target as SVGElement
    const plantGroup = target.closest('.plant-group') as SVGElement
    if (!plantGroup) return

    const placementId = parseInt(plantGroup.dataset.placementId || '0')
    const plantId     = parseInt(plantGroup.dataset.plantId     || '0')

    this.clearSelection({ notifyPanel: false })
    this.selectedPlantId = plantId

    const selectionIndicator = plantGroup.querySelector('.plant-selection-indicator') as SVGElement
    if (selectionIndicator) selectionIndicator.style.display = 'block'

    const nameLabel = plantGroup.querySelector('.plant-name-label') as SVGElement
    if (nameLabel) nameLabel.setAttribute('visibility', 'visible')

    this.dispatch('plantSelected', {
      detail: { placementId, plantId },
      prefix: false,
      bubbles: true
    } as never)
  }

  showPlantInfo(event: Event) {
    if (this.isDragging) return

    const target = event.target as SVGElement
    const plantGroup = target.closest('.plant-group') as SVGElement
    if (!plantGroup) return

    const circle = plantGroup.querySelector('.plant-circle') as SVGCircleElement
    if (circle) {
      const baseR = parseFloat(plantGroup.dataset.baseRadius || circle.getAttribute('r') || '0')
      plantGroup.dataset.baseRadius = baseR.toString()
      circle.setAttribute('r', (baseR * 1.1).toString())
      circle.style.strokeWidth = '4'
    }
  }

  hidePlantInfo(event: Event) {
    if (this.isDragging) return

    const target = event.target as SVGElement
    const plantGroup = target.closest('.plant-group') as SVGElement
    if (!plantGroup) return

    const circle  = plantGroup.querySelector('.plant-circle') as SVGCircleElement
    const plantId = parseInt(plantGroup.dataset.plantId || '0')

    if (circle && plantId !== this.selectedPlantId) {
      const baseR = parseFloat(plantGroup.dataset.baseRadius || circle.getAttribute('r') || '0')
      circle.setAttribute('r', baseR.toString())
      circle.style.strokeWidth = '2'
    }
  }

  // ---------------------------------------------------------------------------
  // Keyboard navigation
  // ---------------------------------------------------------------------------

  handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape': this.clearSelection(); break
      case '+': case '=': event.preventDefault(); this.zoomIn(); break
      case '-': event.preventDefault(); this.zoomOut(); break
      case '0': event.preventDefault(); this.resetZoom(); break
    }
  }

  // ---------------------------------------------------------------------------
  // Pointer drag handlers
  // ---------------------------------------------------------------------------

  private handlePointerDown(event: PointerEvent) {
    const target = event.target as SVGElement
    const plantGroup = target.closest('.plant-group') as SVGGElement | null
    if (!plantGroup) {
      this.clearSelection()
      return
    }

    const circle = plantGroup.querySelector('.plant-circle') as SVGCircleElement | null
    if (!circle) return

    this.hasDragged        = false
    this.isDragging        = false
    this.dragGroup         = plantGroup
    this.dragPointerId     = event.pointerId
    this.dragPlacementId   = parseInt(plantGroup.dataset.placementId || '0')

    const svgPt = this.svgPoint(event)
    this.dragStartSvgX = svgPt.x
    this.dragStartSvgY = svgPt.y
    this.dragGroupOriginalCx = parseFloat(circle.getAttribute('cx') || '0')
    this.dragGroupOriginalCy = parseFloat(circle.getAttribute('cy') || '0')

    // Capture so we keep receiving move/up even if pointer leaves the SVG
    this.svgTarget.setPointerCapture(event.pointerId)
  }

  private handlePointerMove(event: PointerEvent) {
    if (!this.dragGroup || event.pointerId !== this.dragPointerId) return

    const svgPt = this.svgPoint(event)
    const dx = svgPt.x - this.dragStartSvgX
    const dy = svgPt.y - this.dragStartSvgY

    if (!this.hasDragged && Math.hypot(dx, dy) > this.DRAG_THRESHOLD) {
      this.hasDragged = true
      this.isDragging = true
      // Bring the dragged plant to the top of the SVG so it renders above all others
      this.svgTarget.appendChild(this.dragGroup)
    }

    if (!this.isDragging) return

    event.preventDefault()
    this.moveGroupTo(
      this.dragGroup,
      this.dragGroupOriginalCx + dx,
      this.dragGroupOriginalCy + dy
    )
  }

  private async handlePointerUp(event: PointerEvent) {
    if (!this.dragGroup || event.pointerId !== this.dragPointerId) return

    this.svgTarget.releasePointerCapture(event.pointerId)

    if (!this.isDragging) {
      // Was a tap/click — pointer capture prevents the click event from reaching
      // the circle's data-action handler, so handle selection directly.
      const group = this.dragGroup
      this.dragGroup = null
      this.selectPlant({ target: group } as unknown as Event)
      return
    }

    event.preventDefault()
    this.isDragging = false

    const svgPt  = this.svgPoint(event)
    const finalCx = this.dragGroupOriginalCx + (svgPt.x - this.dragStartSvgX)
    const finalCy = this.dragGroupOriginalCy + (svgPt.y - this.dragStartSvgY)

    // Boundary containment check
    if (!this.isInsideBoundary(finalCx, finalCy)) {
      this.snapBack()
      this.dragGroup = null
      errorHandler.showError('Plant must be placed within the garden boundary.')
      return
    }

    // Convert SVG pixel position back to 0-100 grid coordinates
    const grid = this.svgToGrid(finalCx, finalCy)
    if (!grid) {
      this.snapBack()
      this.dragGroup = null
      return
    }

    const clampedX = Math.max(0, Math.min(100, grid.x))
    const clampedY = Math.max(0, Math.min(100, grid.y))

    // Plant is already at the new position (optimistic). Save to backend.
    const group = this.dragGroup
    this.dragGroup = null

    const ok = await this.savePlantPosition(this.dragPlacementId, clampedX, clampedY)
    if (ok) {
      // Persist updated coords in data attrs for future drag snap-backs
      group.dataset.xCoord = clampedX.toString()
      group.dataset.yCoord = clampedY.toString()
      group.dataset.svgCx  = finalCx.toString()
      group.dataset.svgCy  = finalCy.toString()
    } else {
      this.moveGroupTo(group, this.dragGroupOriginalCx, this.dragGroupOriginalCy)
      errorHandler.showError('Failed to save plant position. Please try again.')
    }
  }

  private handlePointerCancel(event: PointerEvent) {
    if (!this.dragGroup || event.pointerId !== this.dragPointerId) return
    this.svgTarget.releasePointerCapture(event.pointerId)
    this.snapBack()
    this.isDragging = false
    this.dragGroup  = null
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Move every SVG child of the plant group to a new center point. */
  private moveGroupTo(group: SVGGElement, cx: number, cy: number) {
    const circle = group.querySelector('.plant-circle') as SVGCircleElement | null
    if (circle) {
      circle.setAttribute('cx', cx.toString())
      circle.setAttribute('cy', cy.toString())
    }

    const indicator = group.querySelector('.plant-selection-indicator') as SVGCircleElement | null
    if (indicator) {
      indicator.setAttribute('cx', cx.toString())
      indicator.setAttribute('cy', cy.toString())
    }

    const label = group.querySelector('.plant-label') as SVGTextElement | null
    if (label) {
      // Marker style positions the name above the pin via data-label-dy; schematic
      // labels sit inside the circle (default +4).
      const labelDy = parseFloat(group.dataset.labelDy || '4')
      label.setAttribute('x', cx.toString())
      label.setAttribute('y', (cy + labelDy).toString())
    }
  }

  private snapBack() {
    if (!this.dragGroup) return
    this.moveGroupTo(this.dragGroup, this.dragGroupOriginalCx, this.dragGroupOriginalCy)
  }

  /** Convert a PointerEvent's client coordinates into SVG user-space coordinates,
   *  accounting for the current viewBox zoom/pan. */
  private svgPoint(event: PointerEvent): { x: number; y: number } {
    const svg = this.svgTarget as unknown as SVGSVGElement
    const pt  = svg.createSVGPoint()
    pt.x = event.clientX
    pt.y = event.clientY
    const ctm = svg.getScreenCTM()
    if (!ctm) return { x: event.clientX, y: event.clientY }
    const svgP = pt.matrixTransform(ctm.inverse())
    return { x: svgP.x, y: svgP.y }
  }

  /** Convert SVG pixel coordinates back to 0-100 grid coordinates. */
  private svgToGrid(svgX: number, svgY: number): { x: number; y: number } | null {
    const t = this.transformValue as SvgTransform | Record<string, never>

    if ('x_offset' in t) {
      const normX = (svgX - t.x_offset) / (t.lng_range * t.scale)
      const normY = 1 - (svgY - t.y_offset) / (t.lat_range * t.scale)
      return { x: normX * 100, y: normY * 100 }
    } else {
      // No boundary — use full padded SVG space (padding=30, usable=340)
      const padding = 30, usable = 340
      return {
        x: ((svgX - padding) / usable) * 100,
        y: (1 - (svgY - padding) / usable) * 100
      }
    }
  }

  /** Ray-casting point-in-polygon test. */
  private isInsideBoundary(svgX: number, svgY: number): boolean {
    const points = this.boundaryPointsValue

    if (!points || points.length === 0) {
      // No polygon — treat the padded SVG area as the boundary
      return svgX >= 30 && svgX <= 370 && svgY >= 30 && svgY <= 370
    }

    let inside = false
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const pi = points[i]!, pj = points[j]!
      const xi = pi.x, yi = pi.y
      const xj = pj.x, yj = pj.y
      const intersect = (yi > svgY) !== (yj > svgY) &&
                        svgX < ((xj - xi) * (svgY - yi)) / (yj - yi) + xi
      if (intersect) inside = !inside
    }
    return inside
  }

  private async savePlantPosition(placementId: number, x: number, y: number): Promise<boolean> {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    try {
      const response = await fetch(
        `/gardens/${this.gardenIdValue}/plant_placements/${placementId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept':       'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ plant_placement: { x_coord: x, y_coord: y } })
        }
      )
      return response.ok
    } catch {
      return false
    }
  }

  private clearSelection(options: { notifyPanel?: boolean } = {}) {
    const notifyPanel = options.notifyPanel !== false
    const hadSelection = this.selectedPlantId !== null

    this.svgTarget.querySelectorAll<SVGElement>('.plant-selection-indicator').forEach(el => {
      el.style.display = 'none'
    })
    this.svgTarget.querySelectorAll<SVGGElement>('.plant-group').forEach(group => {
      const circle = group.querySelector('.plant-circle') as SVGCircleElement | null
      if (!circle) return
      const baseR = group.dataset.baseRadius
      if (baseR) circle.setAttribute('r', baseR)
      circle.style.strokeWidth = '2'
    })
    this.svgTarget.querySelectorAll<SVGElement>('.plant-name-label').forEach(el => {
      el.setAttribute('visibility', 'hidden')
    })
    this.selectedPlantId = null

    if (notifyPanel && hadSelection) {
      this.dispatch('plantSelectionCleared', { prefix: false, bubbles: true } as never)
    }
  }

  private setupSVGViewBox() {
    this.svgTarget.style.width     = '100%'
    this.svgTarget.style.height    = 'auto'
    this.svgTarget.style.maxHeight = '600px'
  }
}
